import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, type IconName } from '@/icons';
import { Dialog, useToast } from '@/ui';
import {
  latestPlatformRelease,
  PLATFORM_LABELS,
  releaseStats,
  type ReleasePlatform,
  type UpdateRelease,
} from '@/core/data/updateLogs';
import { PlatformIcon } from './PlatformIcon';
import { Control, Muted, Select, SmallTitle, Surface, focusRing } from './styles';

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  @media (max-width: 1100px) and (min-width: 700px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
`;
const Panel = styled(Surface)`
  padding: 20px 16px;
  & h2 > span {
    color: ${({ theme }) => theme.color.brandFrom};
  }
`;
const Period = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 12px;
  & select {
    min-height: 30px;
    padding-top: 4px;
    padding-bottom: 4px;
  }
`;
const Stats = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin: 0 0 12px;
`;
const Metric = styled.div<{ $tone: 'brandFrom' | 'success' | 'privacy' }>`
  display: grid;
  grid-template-columns: 29px 1fr;
  column-gap: 9px;
  align-items: center;
  padding: 12px 8px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  & > span {
    color: ${({ theme, $tone }) => theme.color[$tone]};
    grid-row: span 2;
  }
  & dt {
    order: 3;
    grid-column: 2;
    color: ${({ theme }) => theme.color.textMuted};
    font-size: 0.65rem;
    line-height: 1.5;
  }
  & dd {
    order: 2;
    margin: 0;
    font-weight: 650;
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;
const Platforms = styled.ul`
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;
const Platform = styled.li`
  display: grid;
  grid-template-columns: 25px 1fr auto;
  align-items: center;
  gap: 11px;
  padding: 12px 8px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  & > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  & small {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: 0.65rem;
  }
  & > span {
    color: ${({ theme }) => theme.color.textMuted};
    font-variant-numeric: tabular-nums;
    font-size: 0.7rem;
  }
  &[data-platform='android'] > svg {
    color: ${({ theme }) => theme.color.success};
  }
  &[data-platform='web'] > svg {
    color: ${({ theme }) => theme.color.privacy};
  }
`;
const Actions = styled(Surface)`
  display: flex;
  padding: 9px;
  justify-content: space-around;
  gap: 4px;
`;
const Action = styled(Control)`
  flex: 1;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.color.brandFrom};
  & + & {
    border-left: 1px solid ${({ theme }) => theme.color.glassBorder};
    border-radius: 0;
  }
`;
const ShareUrl = styled.input`
  ${focusRing} width: 100%;
  margin: 16px 0;
  padding: 12px;
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

export function ReleaseSidebar({
  releases,
  latest,
}: {
  releases: UpdateRelease[];
  latest: UpdateRelease;
}) {
  const years = [...new Set(releases.map((release) => release.date.slice(0, 4)))];
  const [year, setYear] = useState('all');
  const [shareOpen, setShareOpen] = useState(false);
  const toast = useToast();
  const counts = releaseStats(releases, year);
  const shareUrl = `${window.location.origin}/settings/about#release-${latest.version}`;
  const metrics: {
    label: string;
    value: number;
    icon: IconName;
    tone: 'brandFrom' | 'success' | 'privacy';
  }[] = [
    { label: 'Total updates', value: counts.total, icon: 'file-text', tone: 'brandFrom' },
    { label: 'Improvements', value: counts.improvements, icon: 'trending-up', tone: 'success' },
    { label: 'Fixes', value: counts.fixes, icon: 'wrench', tone: 'privacy' },
    { label: 'Security updates', value: counts.security, icon: 'shield', tone: 'success' },
  ];
  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast('Release link copied');
    } catch {
      toast('Select the link above to copy it manually');
    }
  }
  return (
    <Aside aria-label="Release information">
      <Panel>
        <SmallTitle>
          <span>
            <Icon name="bar-chart-3" size={22} />
          </span>
          Release Stats
        </SmallTitle>
        <Period>
          <Select
            aria-label="Release statistics period"
            value={year}
            onChange={(event) => setYear(event.target.value)}
          >
            <option value="all">All time</option>
            {years.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Period>
        <Stats>
          {metrics.map((metric) => (
            <Metric key={metric.label} $tone={metric.tone}>
              <span>
                <Icon name={metric.icon} size={24} />
              </span>
              <dt>{metric.label}</dt>
              <dd>{metric.value}</dd>
            </Metric>
          ))}
        </Stats>
        <Muted>From published notes. Category totals reflect recorded classifications.</Muted>
      </Panel>
      <Panel>
        <SmallTitle>
          <span>
            <Icon name="database" size={22} />
          </span>
          Platform Coverage
        </SmallTitle>
        <Platforms>
          {(['android', 'ios', 'web'] as ReleasePlatform[]).map((platform) => {
            const release = latestPlatformRelease(releases, platform);
            return (
              <Platform key={platform} data-platform={platform}>
                <PlatformIcon platform={platform} size={23} />
                <div>
                  {PLATFORM_LABELS[platform]}
                  <small>{release ? 'Latest listed release' : 'No published release listed'}</small>
                </div>
                <span>{release ? `v${release.version}` : 'None'}</span>
              </Platform>
            );
          })}
        </Platforms>
      </Panel>
      <Actions aria-label="Release actions">
        <Action
          as={Link}
          to="/settings/notifications"
          aria-label="Notification preferences"
          title="Notification preferences"
        >
          <Icon name="bell" size={22} />
        </Action>
        <Action
          type="button"
          onClick={() => setShareOpen(true)}
          aria-label="Share latest release"
          title="Share latest release"
        >
          <Icon name="share" size={22} />
        </Action>
        <Action as={Link} to="/feedback?submit=bug" aria-label="Report a bug" title="Report a bug">
          <Icon name="flag" size={22} />
        </Action>
        <Action
          as={Link}
          to="/feedback?submit=feature"
          aria-label="Suggest a feature"
          title="Suggest a feature"
        >
          <Icon name="comment" size={22} />
        </Action>
      </Actions>
      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`Share v${latest.version}`}
      >
        <Muted>{latest.title}</Muted>
        <ShareUrl
          readOnly
          aria-label="Release link"
          value={shareUrl}
          onFocus={(event) => event.currentTarget.select()}
        />
        <Control type="button" onClick={() => void copy()}>
          <Icon name="link" size={16} />
          Copy link
        </Control>
      </Dialog>
    </Aside>
  );
}
